import { Component, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Adjustment } from '../../adjustments/adjustment.model';
import { AdjustmentModalTemplateComponent } from '../../adjustments/adjustment-modal-template/adjustment-modal-template.component';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DialogService } from '@services/dialog.service';
import { ReclassificationService } from './reclassification.service';
import * as _ from 'lodash-es';
import { IFinancialItem, IGetReclassParams, ILIFOCalc, IReclassification } from './reclassification.model';
import {
  AVG_BALANCE_SHEET_ASSETS_TAB_NAME,
  LIFO,
  LIFO_ADJ_FIELDS,
  LIFO_ADJ_TYPE,
  READ_ONLY_FIELDS,
  READ_ONLY_HEADER_COLUMNS_MAPPING_ID,
  RECLASS,
  RECLASS_ADJUSTEMENT_TYPES,
  REPLACEMENT,
  VAL_MSG_NOT_ALLOWED_COMBINATION,
  YEAR_END_BALANCE_SHEET_ASSETS_TAB_NAME,
} from './reclassification.const';
import { ReasonFormComponent } from './reason-from/reason-form.component';
import { forkJoin, ReplaySubject, Subject } from 'rxjs';
import { delay, takeUntil } from 'rxjs/operators';
import { ReclassificationCalcService } from './reclassification-calc.service';

export interface IFormField {
  formControlName: string;
  placeholder: string;
  value?: any;
  readonly?: boolean;
}

export const LIFO_REVERSE_NOT_PRESENT = 'Since here is no LIFO Reserve data, you will not be able to proceed with LIFO Adjustment.';

@Component({
  selector: 'app-reclassification',
  templateUrl: './reclassification.component.html',
  styleUrls: ['./reclassification.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class ReclassificationComponent implements OnInit, OnDestroy {
  @ViewChild(AdjustmentModalTemplateComponent, { static: false }) modal: AdjustmentModalTemplateComponent;
  @Input() readOnly = false;
  @Input() selectedYear: number;
  @Input() years: string[];
  @Input() results: any;
  @Input() title: string;
  @Input() sourceName = 'Source';
  @Input() LIFOReserveValues: any;
  @Input() sectionName = 'sectionName not provided';
  @Input() comparableId: number;
  @Input() databaseId: number;
  @Input() studySearchStrategyId: number;
  @Input() isFunctionalCurrency: boolean;

  public deleteDisabled: boolean;
  public spinnerRS = new ReplaySubject<boolean>(1);
  public spinnerVisible$ = this.spinnerRS.asObservable().pipe(delay(1));

  private destroy$ = new Subject();

  lifoReserveAdjAvailable = true;
  noLifoMsg = LIFO_REVERSE_NOT_PRESENT;

  private formDefinition: any;
  public lifoFormDefinition: any;
  private calcClicked = false;
  adjustmentTypes = RECLASS_ADJUSTEMENT_TYPES;
  formFields: IFormField[] = [];
  headerColumns: IFormField[] = [];

  replacementForm: UntypedFormGroup;
  lifoForm: UntypedFormGroup;
  reclassificationForm: UntypedFormGroup;
  adjustmentForm: UntypedFormGroup;
  finalForm: UntypedFormGroup;
  calculatedLifoAdjValues: ILIFOCalc[];

  averageAssetsSectionName = AVG_BALANCE_SHEET_ASSETS_TAB_NAME;

  reasonTypes = {
    replacement: REPLACEMENT,
    reclassification: RECLASS,
    LIFO: LIFO,
  };

  private replacementReason: string;
  private lIFOReason: string;
  private reclassificationReason: string;

  errorMsg_adjCombNotAllowed = VAL_MSG_NOT_ALLOWED_COMBINATION;

  constructor(
    private reclassificationService: ReclassificationService,
    private fb: UntypedFormBuilder,
    private dialogService: DialogService,
    private reclassificationCalcService: ReclassificationCalcService
  ) {
    this.reclassificationForm = this.fb.group({}, { updateOn: 'change' });
  }

  ngOnInit() {
    this.calculatedLifoAdjValues = this.reclassificationCalcService.calcLIFO(this.LIFOReserveValues, this.years, this.sourceName);

    this.formDefinition = this.getFormDefinition();

    this.initializeForms();
  }

  initializeForms() {
    this.spinnerRS.next(true);
    this.finalForm = this.fb.group(this.formDefinition, { updateOn: 'change' });
    this.reclassificationForm = this.fb.group(this.formDefinition, { updateOn: 'change' });
    this.replacementForm = this.fb.group(this.formDefinition, { updateOn: 'change' });
    this.lifoFormDefinition = this.getFormDefinitionLifo(this.formDefinition);
    this.lifoForm = this.fb.group(this.lifoFormDefinition, { updateOn: 'change' });
    this.adjustmentForm = this.fb.group(
      {
        adjustmentTypes: [null, [Validators.required]],
      },
      { updateOn: 'change' }
    );
    this.getValuesFromDBAndPatch();

    this.lifoReserveAdjAvailable = this.showLifo(this.selectedYear);

    if (!this.lifoReserveAdjAvailable) {
      this.adjustmentTypes = RECLASS_ADJUSTEMENT_TYPES.filter((a) => a.displayText !== LIFO);
    } else {
      this.adjustmentTypes = RECLASS_ADJUSTEMENT_TYPES;
    }
    this.subscribeToFormChanges();
  }

  subscribeToFormChanges(): void {
    this.reclassificationForm.valueChanges.subscribe((newval: any) => {
      const autoCalc = this.reclassificationCalcService.autoCalculations(newval, true);
      this.reclassificationForm.patchValue(autoCalc, { emitEvent: false, onlySelf: true });
      this.calcClicked = false;
    });
    this.replacementForm.valueChanges.subscribe((newval: any) => {
      const autoCalc = this.reclassificationCalcService.autoCalculations(newval, true);
      this.replacementForm.patchValue(autoCalc, { emitEvent: false, onlySelf: true });
      this.calcClicked = false;
    });

    this.adjustmentForm.valueChanges.subscribe(() => {
      this.calcClicked = false;
      this.finalForm.reset();
    });
  }

  getValuesFromDBAndPatch() {
    const params: IGetReclassParams = {
      comparableId: this.comparableId,
      sectionName: this.sectionName,
      databaseId: this.databaseId,
      sourceName: this.sourceName,
      studySearchStrategyId: this.studySearchStrategyId,
      year: this.selectedYear,
    };

    if (this.sectionName === AVG_BALANCE_SHEET_ASSETS_TAB_NAME) {
      params.sectionName = YEAR_END_BALANCE_SHEET_ASSETS_TAB_NAME;
      const params_PY = { ...params, year: +this.selectedYear - 1 };
      forkJoin([this.reclassificationService.get(params), this.reclassificationService.get(params_PY)])
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          ([cy, py]) => {
            const avgResult = this.reclassificationCalcService.calculateAverages(
              cy.finalValue,
              py.finalValue,
              this.getBaseFinancialValues(this.selectedYear, false),
              this.getBaseFinancialValues(+this.selectedYear - 1, false)
            );
            if (avgResult != null) {
              this.finalForm.patchValue(avgResult);
            }
            this.spinnerRS.next(false);
          },
          () => {
            this.spinnerRS.next(false);
          }
        );
    } else {
      this.reclassificationService
        .get(params)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (result) => {
            this.patchResult(result);
            this.spinnerRS.next(false);
          },
          () => {
            this.spinnerRS.next(false);
          }
        );
    }
  }

  patchResult(result: IReclassification) {
    const selectedAdjustment: any[] = [];

    this.lIFOReason = result.lifoReason;
    this.reclassificationReason = result.reclassificationReason;
    this.replacementReason = result.replacementReason;

    if (result.replacement) {
      selectedAdjustment.push(RECLASS_ADJUSTEMENT_TYPES.find((at) => at.displayText === REPLACEMENT));
      this.replacementForm.patchValue(this.reclassificationCalcService.mapFinancialItemToPatchObject(result.replacement));
    }

    if (result.lifoAdjustment) {
      selectedAdjustment.push(RECLASS_ADJUSTEMENT_TYPES.find((at) => at.displayText === LIFO));
      // no patch here values auto calculated and based on lifo reserve;
      // if we mapped values from result user would not be able to recalculate values
    }

    if (result.reclassification) {
      selectedAdjustment.push(RECLASS_ADJUSTEMENT_TYPES.find((at) => at.displayText === RECLASS));
      this.reclassificationForm.patchValue(this.reclassificationCalcService.mapFinancialItemToPatchObject(result.reclassification));
    }

    if (result.finalValue) {
      this.finalForm.patchValue(this.reclassificationCalcService.mapFinancialItemToPatchObject(result.finalValue));
    }

    if (selectedAdjustment.length > 0) {
      this.calcClicked = true;
    }

    this.deleteDisabled = selectedAdjustment.length === 0;

    this.adjustmentFormPatchValue(selectedAdjustment);
  }

  adjustmentFormPatchValue(adjTypes) {
    this.adjustmentForm.patchValue({ adjustmentTypes: adjTypes }, { emitEvent: false, onlySelf: true });
  }

  getFormDefinitionLifo(formDefinition) {
    const newFormDef = { ...formDefinition };
    LIFO_ADJ_FIELDS.forEach((f) => {
      if (Object.prototype.hasOwnProperty.call(newFormDef, f.mappingId)) {
        newFormDef[f.mappingId] = [this.getLifoValueForField(this.selectedYear, f.lifoType)];
      }
    });
    return newFormDef;
  }

  showLifo(year: number) {
    const lifoVal = this.calculatedLifoAdjValues.find((lv) => +lv.year === +year);
    if (lifoVal !== undefined) {
      if (lifoVal.lifoReserve != null || lifoVal.lifoAdj != null) {
        return true;
      }
    }
    return false;
  }

  getLifoValueForField(year: number, lifoType: string) {
    let retVal = 0;
    const lifoVal = this.calculatedLifoAdjValues.find((lv) => +lv.year === +year);
    if (lifoVal !== undefined) {
      switch (lifoType) {
        case LIFO_ADJ_TYPE.LR:
          retVal = +lifoVal.lifoReserve;
          break;
        case LIFO_ADJ_TYPE.LA:
          retVal = +lifoVal.lifoAdj;
          break;
        case LIFO_ADJ_TYPE.LAN:
          retVal = +lifoVal.lifoAdjNegative;
          break;
      }
    }
    return retVal.toFixed(2);
  }

  private getFormDefinition() {
    const retVal = {};

    this.results.forEach((f) => {
      const fieldName =
        f.vendorAccountMappingId === undefined ? this.reclassificationCalcService.camelize(f.vendorAccountName) : f.vendorAccountMappingId;
      /* transfer header columns to different headerColumns array */
      if (READ_ONLY_HEADER_COLUMNS_MAPPING_ID.indexOf(fieldName) !== -1) {
        this.headerColumns.push({ formControlName: fieldName, placeholder: f.vendorAccountName, value: f });
      } else {
        const readOnly = this.readOnly ? true : READ_ONLY_FIELDS.indexOf(fieldName) !== -1;

        this.formFields.push({ formControlName: fieldName, placeholder: f.vendorAccountName, value: f, readonly: readOnly });
        retVal[fieldName] = [null];
      }
    });

    return retVal;
  }

  close(adjustment: Adjustment = null) {
    this.modal.close(adjustment);
  }

  onSubmit() {
    const selectedAdjustments = this.adjustmentForm.value.adjustmentTypes;
    const reclassification = this.reclassificationForm.value;
    const lifo = this.lifoForm.value;

    const replacement = this.replacementForm.value;
    const final = this.finalForm.value;
    const reclassResult: IReclassification = {
      comparableID: this.comparableId,
      sourceName: this.sourceName,
      databaseId: this.databaseId,
      studySearchStrategyId: this.studySearchStrategyId,
      year: this.selectedYear,
      replacement: null,
      lifoAdjustment: null,
      reclassification: null,
      finalValue: final,
      lifoReason: this.lIFOReason,
      reclassificationReason: this.reclassificationReason,
      replacementReason: this.replacementReason,
      sectionName: this.sectionName,
      inFunctionalCurrency: this.isFunctionalCurrency,
    };

    if (this.sectionVisible(REPLACEMENT, selectedAdjustments)) {
      reclassResult.replacement = replacement;
    }

    if (this.sectionVisible(LIFO, selectedAdjustments)) {
      reclassResult.lifoAdjustment = lifo;
    }

    if (this.sectionVisible(RECLASS, selectedAdjustments)) {
      reclassResult.reclassification = reclassification;
    }

    if (reclassResult.replacement == null && reclassResult.lifoAdjustment == null && reclassResult.reclassification == null) {
      reclassResult.finalValue = null;
    }

    /* conversion required to simplify backend save */
    const finalResult: IReclassification = {
      ...reclassResult,
      base: null,
      finalValue: this.mapFinanceToFinancialItem(reclassResult.finalValue),
      replacement: this.mapFinanceToFinancialItem(reclassResult.replacement),
      reclassification: this.mapFinanceToFinancialItem(reclassResult.reclassification),
      lifoAdjustment: this.mapFinanceToFinancialItem(reclassResult.lifoAdjustment),
    };

    this.reclassificationService.post(finalResult).subscribe(() => {
      this.dialogService.acknowledge('Reclassification', 'Reclass data saved.').then(() => this.close(null));
    });
  }

  mapFinanceToFinancialItem(financials: any): IFinancialItem[] {
    if (financials == null) {
      return null;
    }

    const retVal: IFinancialItem[] = [];
    Object.keys(financials).forEach((k) => {
      retVal.push({ value: financials[k], accountMappingId: +k });
    });

    return retVal;
  }

  sectionVisible(sectionName, selAdjustments) {
    return selAdjustments != null ? selAdjustments.find((sa) => sa.displayText === sectionName) !== undefined : false;
  }

  /* mk todo convert into validator function */
  adjCombinationValid(selAdjustments) {
    if (selAdjustments == null) {
      return true;
    }
    if (selAdjustments.length > 1 && selAdjustments.find((sa) => sa.displayText === REPLACEMENT)) {
      return false;
    }
    return true;
  }

  adjSelected(selAdjustments) {
    return selAdjustments != null ? true : false;
  }

  removedMetrics(sectionName) {
    switch (sectionName) {
      case REPLACEMENT: {
        if (this.replacementReason && this.replacementReason.length > 0) {
          this.replacementReason = null;
        }
        break;
      }
      case LIFO: {
        if (this.lIFOReason && this.lIFOReason.length > 0) {
          this.lIFOReason = null;
        }
        break;
      }
      case RECLASS: {
        if (this.reclassificationReason && this.reclassificationReason.length > 0) {
          this.reclassificationReason = null;
        }
        break;
      }
      default: {
        break;
      }
    }
    this.calcClicked = false;
    this.finalForm.reset();
    const removedAdjustment = this.adjustmentForm.value.adjustmentTypes.filter((a) => a.displayText !== sectionName);
    this.adjustmentFormPatchValue(removedAdjustment);
  }

  calculateAdjustment() {
    const selectedAdjustments = this.adjustmentForm.value.adjustmentTypes;
    const reclassification = this.reclassificationForm.value;
    const lifo = this.lifoForm.value;
    const replacement = this.replacementForm.value;

    let final = this.finalForm.value;
    final = this.getBaseFinancialValues(this.selectedYear);

    if (this.sectionVisible(REPLACEMENT, selectedAdjustments)) {
      final = this.reclassificationCalcService.calcReplacement(final, replacement);
      final = this.reclassificationCalcService.autoCalculations(final);
    }

    if (this.sectionVisible(LIFO, selectedAdjustments)) {
      final = this.reclassificationCalcService.addValues(final, lifo);
    }

    if (this.sectionVisible(RECLASS, selectedAdjustments)) {
      final = this.reclassificationCalcService.addValues(final, reclassification);
      final = this.reclassificationCalcService.autoCalculations(final);
    }

    this.finalForm.patchValue(final);
    this.calcClicked = true;
  }

  getBaseFinancialValues(year: number, convertNullToZero = true) {
    const orgValues = {};
    this.formFields.forEach((ff) => {
      const value = this.getValueByPropertyName(
        year,
        this.results.find((r) => r.vendorAccountName === ff.placeholder),
        convertNullToZero
      );
      orgValues[ff.formControlName] = value;
    });
    return orgValues;
  }

  getValueByPropertyName(syear: number, val: any, convertNullToZero = true) {
    const sName = this.sourceName == null ? '' : this.sourceName.toLowerCase() + '_';
    return convertNullToZero ? this.reclassificationCalcService.zeroIfNA(val[sName + syear]) : val[sName + syear];
  }

  tabChanged(e: any) {
    this.selectedYear = +this.years[e.index];
    this.initializeForms();
    this.calcClicked = false;
  }

  openReasonModal(reasonType: string, reasonText: string) {
    const modalRef = this.dialogService.launchDialog(ReasonFormComponent, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'right in',
      centered: false,
      size: <any>'xl',
    });
    modalRef.componentInstance.readOnly = this.readOnly;
    modalRef.componentInstance.reasonType = reasonType;
    modalRef.componentInstance.reasonText = reasonText;

    modalRef.result.then((retVal) => {
      if (retVal) {
        switch (retVal.reasonType) {
          case this.reasonTypes.reclassification:
            this.reclassificationReason = retVal.reasonText;
            break;
          case this.reasonTypes.replacement:
            this.replacementReason = retVal.reasonText;
            break;
          case this.reasonTypes.LIFO:
            this.lIFOReason = retVal.reasonText;
            break;
        }

        return;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  submitDisabled() {
    if (this.readOnly) {
      return true;
    }

    if (this.adjustmentsRemoved()) {
      return false;
    }

    return !this.calcClicked || !this.adjustmentForm.valid || !this.adjCombinationValid(this.adjustmentForm.controls['adjustmentTypes'].value);
  }

  adjustmentsRemoved() {
    return !this.deleteDisabled && _.isEmpty(this.adjustmentForm.controls['adjustmentTypes'].value);
  }
}
